import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50 dark:bg-gray-900" }
const _hoisted_2 = { class: "flex items-center justify-between" }
const _hoisted_3 = { class: "flex items-center space-x-3" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "p-6 space-y-6" }
const _hoisted_6 = { class: "flex items-center" }
const _hoisted_7 = { class: "text-sm text-blue-800 dark:text-blue-200" }
const _hoisted_8 = {
  key: 1,
  class: "grid grid-cols-1 md:grid-cols-4 gap-4"
}
const _hoisted_9 = { class: "text-2xl font-bold text-gray-900 dark:text-white" }
const _hoisted_10 = { class: "text-2xl font-bold text-green-600 dark:text-green-400" }
const _hoisted_11 = { class: "text-2xl font-bold text-red-600 dark:text-red-400" }
const _hoisted_12 = { class: "text-2xl font-bold text-yellow-600 dark:text-yellow-400" }
const _hoisted_13 = { class: "flex" }
const _hoisted_14 = { class: "ml-3" }
const _hoisted_15 = { class: "mt-2 text-sm text-red-700 dark:text-red-300" }
const _hoisted_16 = {
  key: 4,
  class: "space-y-4"
}
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { class: "p-4" }
const _hoisted_19 = { class: "flex items-center justify-between" }
const _hoisted_20 = { class: "flex items-center space-x-4" }
const _hoisted_21 = { class: "mr-1" }
const _hoisted_22 = { class: "flex items-center space-x-2" }
const _hoisted_23 = { class: "font-mono text-sm font-medium text-gray-900 dark:text-white" }
const _hoisted_24 = { class: "text-gray-500 dark:text-gray-400" }
const _hoisted_25 = { class: "text-sm text-gray-500 dark:text-gray-400" }
const _hoisted_26 = { class: "flex items-center space-x-4" }
const _hoisted_27 = { class: "flex items-center space-x-3 text-sm" }
const _hoisted_28 = {
  key: 0,
  class: "flex items-center text-red-600 dark:text-red-400"
}
const _hoisted_29 = {
  key: 1,
  class: "flex items-center text-yellow-600 dark:text-yellow-400"
}
const _hoisted_30 = {
  key: 0,
  class: "hidden md:flex flex-wrap gap-1 max-w-xs"
}
const _hoisted_31 = {
  key: 0,
  class: "px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded"
}
const _hoisted_32 = {
  key: 6,
  class: "flex justify-center"
}

import { ref, reactive, computed, onMounted, inject, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTheme } from '../../utils/useTheme'
import QualityGatesFilters from './components/QualityGatesFilters.vue'
import NetworkAccessHint from '../../components/shared/NetworkAccessHint.vue'
import { qualityGatesService, leaderboardService } from '../../services/quality-gates'
import type {
  QualityGatePR,
  QualityGatesFilters as QGFilters,
  QualityGatesQueryParams
} from '../../types/quality-gates'
import { logger } from '../../utils/logger'


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const theme = inject('theme', useTheme())

// State
const prs = ref<QualityGatePR[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Team filter from URL (for leaderboard drill-down)
const teamFilter = ref<string | null>(null)
const subTeamFilter = ref<string | null>(null)

// Filters
const filters = reactive<QGFilters>({
  repository: '',
  status: [],
  gate: ''
})

// Pagination
const pagination = reactive({
  total: 0,
  hasMore: false,
  nextCursor: undefined as string | undefined
})

// Computed stats
const stats = computed(() => {
  return {
    total: prs.value.length,
    pass: prs.value.filter((pr) => pr.status === 'pass').length,
    fail: prs.value.filter((pr) => pr.status === 'fail').length,
    warning: prs.value.filter((pr) => pr.status === 'warning').length
  }
})

// Methods
const fetchPRs = async (params: QualityGatesQueryParams = {}, append = false) => {
  try {
    loading.value = true
    error.value = null

    logger.log('Fetching quality gates with params:', params)

    const response = await qualityGatesService.fetchPRs(params)

    if (append) {
      prs.value.push(...response.data)
    } else {
      prs.value = response.data
    }

    pagination.total = response.pagination.total
    pagination.hasMore = response.pagination.hasMore
    pagination.nextCursor = response.pagination.nextCursor

    logger.log('Quality gates fetched:', {
      count: response.data.length,
      total: response.pagination.total
    })
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to fetch quality gates'
    logger.error('Error fetching quality gates:', err)
  } finally {
    loading.value = false
  }
}

const refreshPRs = () => {
  const params = buildQueryParams()
  fetchPRs(params, false)
}

const loadMore = () => {
  if (!pagination.nextCursor) return

  const params = buildQueryParams()
  params.cursor = pagination.nextCursor
  fetchPRs(params, true)
}

const handleApplyFilters = (newFilters: QGFilters) => {
  // Use the filters passed from the event, not the ref (which may not be updated yet)
  const params: QualityGatesQueryParams = {
    limit: 20
  }

  if (newFilters.repository) params.repository = newFilters.repository
  if (newFilters.status.length === 1) params.status = newFilters.status[0]
  if (newFilters.gate) params.gate = newFilters.gate

  logger.log('Applying filters:', newFilters, 'Query params:', params)
  fetchPRs(params, false)
}

const buildQueryParams = (): QualityGatesQueryParams => {
  const params: QualityGatesQueryParams = {
    limit: 20
  }

  if (filters.repository) params.repository = filters.repository
  if (filters.status.length === 1) params.status = filters.status[0]
  if (filters.gate) params.gate = filters.gate

  // Include team/subTeam filters from URL (leaderboard drill-down)
  if (teamFilter.value) (params as any).team = teamFilter.value
  if (subTeamFilter.value) (params as any).subTeam = subTeamFilter.value

  return params
}

const navigateToPR = (pr: QualityGatePR) => {
  router.push(`/quality-gates/${encodeURIComponent(pr.repository)}/${pr.pr_number}`)
}

const clearTeamFilter = () => {
  teamFilter.value = null
  subTeamFilter.value = null
  // Remove query params from URL
  router.replace({ path: '/quality-gates', query: {} })
  refreshPRs()
}

// Format team name for display
const getTeamDisplayName = () => {
  if (subTeamFilter.value) {
    return leaderboardService.formatSubTeamName(subTeamFilter.value)
  }
  if (teamFilter.value) {
    return leaderboardService.formatTeamName(teamFilter.value)
  }
  return ''
}

// Lifecycle
onMounted(() => {
  // Check for team/subTeam query params (from leaderboard drill-down)
  const team = route.query.team as string | undefined
  const subTeam = route.query.subTeam as string | undefined

  if (team) {
    teamFilter.value = team
    logger.log('Filtering by team from URL:', team)
  }
  if (subTeam) {
    subTeamFilter.value = subTeam
    logger.log('Filtering by subTeam from URL:', subTeam)
  }

  fetchPRs(buildQueryParams())
})

// Watch for route changes (e.g., navigating back from detail page)
watch(
  () => route.query,
  (newQuery) => {
    const team = newQuery.team as string | undefined
    const subTeam = newQuery.subTeam as string | undefined

    if (team !== teamFilter.value || subTeam !== subTeamFilter.value) {
      teamFilter.value = team || null
      subTeamFilter.value = subTeam || null
      fetchPRs(buildQueryParams())
    }
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass([
        _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ])
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", null, [
          _createElementVNode("h1", {
            class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-100' : 'text-gray-900', 'text-2xl font-bold'])
          }, " Quality Gates ", 2),
          _createElementVNode("p", {
            class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-1 text-sm'])
          }, " View quality gate results for all pull requests ", 2)
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("button", {
            disabled: loading.value,
            class: _normalizeClass([
              _unref(theme).isDark.value
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-white hover:bg-gray-50 text-gray-700',
              'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors flex items-center',
              loading.value ? 'opacity-50 cursor-not-allowed' : ''
            ]),
            onClick: refreshPRs
          }, [
            (_openBlock(), _createElementBlock("svg", {
              class: _normalizeClass([loading.value ? 'animate-spin' : '', 'w-4 h-4 mr-2']),
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, _cache[1] || (_cache[1] = [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              }, null, -1)
            ]), 2)),
            _createTextVNode(" " + _toDisplayString(loading.value ? 'Loading...' : 'Refresh'), 1)
          ], 10, _hoisted_4)
        ])
      ])
    ], 2),
    _createElementVNode("div", _hoisted_5, [
      (teamFilter.value || subTeamFilter.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200',
          'border rounded-md p-3 flex items-center justify-between'
        ])
          }, [
            _createElementVNode("div", _hoisted_6, [
              _cache[3] || (_cache[3] = _createElementVNode("svg", {
                class: "h-5 w-5 text-blue-400 mr-2",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                })
              ], -1)),
              _createElementVNode("span", _hoisted_7, [
                _cache[2] || (_cache[2] = _createTextVNode(" Showing PRs for ")),
                _createElementVNode("strong", null, _toDisplayString(getTeamDisplayName()), 1)
              ])
            ]),
            _createElementVNode("button", {
              class: _normalizeClass([
            _unref(theme).isDark.value
              ? 'text-blue-300 hover:text-blue-100'
              : 'text-blue-600 hover:text-blue-800',
            'text-sm font-medium flex items-center'
          ]),
              onClick: clearTeamFilter
            }, _cache[4] || (_cache[4] = [
              _createTextVNode(" Clear Filter "),
              _createElementVNode("svg", {
                class: "h-4 w-4 ml-1",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M6 18L18 6M6 6l12 12"
                })
              ], -1)
            ]), 2)
          ], 2))
        : _createCommentVNode("", true),
      _createVNode(QualityGatesFilters, {
        filters: filters,
        "onUpdate:filters": _cache[0] || (_cache[0] = ($event: any) => ((filters) = $event)),
        onApplyFilters: handleApplyFilters
      }, null, 8, ["filters"]),
      (prs.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("div", {
              class: _normalizeClass([
            _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4'
          ])
            }, [
              _createElementVNode("div", _hoisted_9, _toDisplayString(stats.value.total), 1),
              _cache[5] || (_cache[5] = _createElementVNode("div", { class: "text-sm text-gray-500 dark:text-gray-400" }, "Total PRs", -1))
            ], 2),
            _createElementVNode("div", {
              class: _normalizeClass([
            _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4'
          ])
            }, [
              _createElementVNode("div", _hoisted_10, _toDisplayString(stats.value.pass), 1),
              _cache[6] || (_cache[6] = _createElementVNode("div", { class: "text-sm text-gray-500 dark:text-gray-400" }, "Passing", -1))
            ], 2),
            _createElementVNode("div", {
              class: _normalizeClass([
            _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4'
          ])
            }, [
              _createElementVNode("div", _hoisted_11, _toDisplayString(stats.value.fail), 1),
              _cache[7] || (_cache[7] = _createElementVNode("div", { class: "text-sm text-gray-500 dark:text-gray-400" }, "Failing", -1))
            ], 2),
            _createElementVNode("div", {
              class: _normalizeClass([
            _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4'
          ])
            }, [
              _createElementVNode("div", _hoisted_12, _toDisplayString(stats.value.warning), 1),
              _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-sm text-gray-500 dark:text-gray-400" }, "Warnings", -1))
            ], 2)
          ]))
        : _createCommentVNode("", true),
      (error.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200',
          'border rounded-md p-4'
        ])
          }, [
            _createElementVNode("div", _hoisted_13, [
              _cache[10] || (_cache[10] = _createElementVNode("div", { class: "flex-shrink-0" }, [
                _createElementVNode("svg", {
                  class: "h-5 w-5 text-red-400",
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  })
                ])
              ], -1)),
              _createElementVNode("div", _hoisted_14, [
                _cache[9] || (_cache[9] = _createElementVNode("h3", { class: "text-sm font-medium text-red-800 dark:text-red-200" }, " Error loading quality gates ", -1)),
                _createElementVNode("div", _hoisted_15, _toDisplayString(error.value), 1),
                _createVNode(NetworkAccessHint),
                _createElementVNode("button", {
                  class: "mt-2 px-3 py-1 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100 rounded text-sm",
                  onClick: refreshPRs
                }, " Try Again ")
              ])
            ])
          ], 2))
        : _createCommentVNode("", true),
      (loading.value && prs.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", {
            key: 3,
            class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8'
        ])
          }, _cache[11] || (_cache[11] = [
            _createStaticVNode("<div class=\"flex items-center justify-center\"><svg class=\"animate-spin h-8 w-8 text-blue-500\" fill=\"none\" viewBox=\"0 0 24 24\"><circle class=\"opacity-25\" cx=\"12\" cy=\"12\" r=\"10\" stroke=\"currentColor\" stroke-width=\"4\"></circle><path class=\"opacity-75\" fill=\"currentColor\" d=\"M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z\"></path></svg><span class=\"ml-3 text-lg text-gray-600 dark:text-gray-300\">Loading quality gate results...</span></div>", 1)
          ]), 2))
        : (prs.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prs.value, (pr) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: `${pr.repository}-${pr.pr_number}`,
                  class: _normalizeClass([
            _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer',
            `border-l-4 ${_unref(qualityGatesService).getStatusBorderColor(pr.status)}`
          ]),
                  onClick: ($event: any) => (navigateToPR(pr))
                }, [
                  _createElementVNode("div", _hoisted_18, [
                    _createElementVNode("div", _hoisted_19, [
                      _createElementVNode("div", _hoisted_20, [
                        _createElementVNode("span", {
                          class: _normalizeClass([
                    _unref(qualityGatesService).getStatusColor(pr.status),
                    'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium'
                  ])
                        }, [
                          _createElementVNode("span", _hoisted_21, _toDisplayString(_unref(qualityGatesService).getStatusIcon(pr.status)), 1),
                          _createTextVNode(" " + _toDisplayString(pr.status.toUpperCase()), 1)
                        ], 2),
                        _createElementVNode("div", null, [
                          _createElementVNode("div", _hoisted_22, [
                            _createElementVNode("span", _hoisted_23, _toDisplayString(pr.repository), 1),
                            _createElementVNode("span", _hoisted_24, "#" + _toDisplayString(pr.pr_number), 1)
                          ]),
                          _createElementVNode("div", _hoisted_25, _toDisplayString(_unref(qualityGatesService).formatRelativeTime(pr.last_updated)) + " · " + _toDisplayString(pr.commit_sha.substring(0, 7)), 1)
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_26, [
                        _createElementVNode("div", _hoisted_27, [
                          (pr.total_errors > 0)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_28, [
                                _cache[12] || (_cache[12] = _createElementVNode("svg", {
                                  class: "w-4 h-4 mr-1",
                                  fill: "currentColor",
                                  viewBox: "0 0 20 20"
                                }, [
                                  _createElementVNode("path", {
                                    "fill-rule": "evenodd",
                                    d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z",
                                    "clip-rule": "evenodd"
                                  })
                                ], -1)),
                                _createTextVNode(" " + _toDisplayString(pr.total_errors) + " errors ", 1)
                              ]))
                            : _createCommentVNode("", true),
                          (pr.total_warnings > 0)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_29, [
                                _cache[13] || (_cache[13] = _createElementVNode("svg", {
                                  class: "w-4 h-4 mr-1",
                                  fill: "currentColor",
                                  viewBox: "0 0 20 20"
                                }, [
                                  _createElementVNode("path", {
                                    "fill-rule": "evenodd",
                                    d: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z",
                                    "clip-rule": "evenodd"
                                  })
                                ], -1)),
                                _createTextVNode(" " + _toDisplayString(pr.total_warnings) + " warnings ", 1)
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        (pr.failing_gates.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pr.failing_gates.slice(0, 2), (gate) => {
                                return (_openBlock(), _createElementBlock("span", {
                                  key: gate,
                                  class: "px-2 py-0.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-xs rounded"
                                }, _toDisplayString(gate), 1))
                              }), 128)),
                              (pr.failing_gates.length > 2)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_31, " +" + _toDisplayString(pr.failing_gates.length - 2) + " more ", 1))
                                : _createCommentVNode("", true)
                            ]))
                          : _createCommentVNode("", true),
                        _cache[14] || (_cache[14] = _createElementVNode("svg", {
                          class: "w-5 h-5 text-gray-400",
                          fill: "none",
                          stroke: "currentColor",
                          viewBox: "0 0 24 24"
                        }, [
                          _createElementVNode("path", {
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round",
                            "stroke-width": "2",
                            d: "M9 5l7 7-7 7"
                          })
                        ], -1))
                      ])
                    ])
                  ])
                ], 10, _hoisted_17))
              }), 128))
            ]))
          : (!loading.value && !error.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 5,
                class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8 text-center'
        ])
              }, _cache[15] || (_cache[15] = [
                _createElementVNode("svg", {
                  class: "mx-auto h-12 w-12 text-gray-400",
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  })
                ], -1),
                _createElementVNode("h3", { class: "mt-2 text-sm font-medium text-gray-900 dark:text-white" }, "No results found", -1),
                _createElementVNode("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, " No quality gate results match your current filters. ", -1)
              ]), 2))
            : _createCommentVNode("", true),
      (pagination.hasMore && !loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
            _createElementVNode("button", {
              class: _normalizeClass([
            _unref(theme).isDark.value
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              : 'bg-white hover:bg-gray-50 text-gray-700',
            'px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors'
          ]),
              onClick: loadMore
            }, " Load More ", 2)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})